/**
 * A person's own UI preferences — theme, editor and drive-display settings.
 *
 * ── Why this lives in the dependency-free package ───────────────────────
 *
 * Three packages need the SAME vocabulary and none of them can reach the
 * others: `@papan/database` types the `users.settings` JSONB column with it,
 * `@papan/auth` validates a `PATCH /api/users/me/settings` body against it, and
 * `@papan/core-ui` renders the settings screen from it. `shared/core-ui` does
 * not depend on `@papan/database` and must not start; the schema module cannot
 * import React. So this is the one home all three can share, which is the same
 * reasoning that put `identity-only.ts` and `plan-source.ts` here.
 *
 * ── Closed lists are RUNTIME values, not just types ─────────────────────
 *
 * `THEME_MODES` and `DEFAULT_VIEWS` are `as const` arrays with the union
 * DERIVED from them, so the validator and the type cannot drift: adding a
 * fourth theme is one edit, and forgetting the validator becomes impossible
 * rather than merely unlikely. A type alone would be erased before the request
 * body ever reached it.
 *
 * ── These fields were a FILE until issue #369 ───────────────────────────
 *
 * They lived in `settings.json` inside the person's personal tenant, under the
 * `account-config` write exemption — the one write an identity-only tenant
 * still permitted. Moving them onto `users.settings` removes that exemption and
 * closes #245 (a whole-object PUT built from a stale closure silently reverting
 * a second writer's theme change) in the same pass. `language` did NOT survive
 * the move: it is reset to English, not relocated. See
 * docs/plans/2026-08-14_LANE_A_IMPLEMENTATION_PLAN.md.
 */

/** The three themes. Ordered dark → light; the middle one is the default. */
export const THEME_MODES = ['dark', 'dim', 'light'] as const
export type ThemeMode = (typeof THEME_MODES)[number]

export function isThemeMode(v: unknown): v is ThemeMode {
  return typeof v === 'string' && (THEME_MODES as readonly string[]).includes(v)
}

/**
 * Per-area themes.
 *
 * `syncAll` is not redundant with "the three values happen to match": it says
 * the areas are LINKED, so changing one changes all three. Dropping it and
 * inferring the state from equality would silently unlink a user who had
 * deliberately set all three the same.
 */
export interface ThemeSettings {
  syncAll: boolean
  drive: ThemeMode
  workspace: ThemeMode
  chat: ThemeMode
}

export function isThemeSettings(v: unknown): v is ThemeSettings {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false
  const t = v as Record<string, unknown>
  // Whole-object, every member required. A partial theme is not a theme: the
  // column takes it as one unit (jsonb `||` replaces the key wholesale), so a
  // half-object would leave the missing areas undefined and render unthemed.
  return typeof t.syncAll === 'boolean'
    && isThemeMode(t.drive) && isThemeMode(t.workspace) && isThemeMode(t.chat)
}

/** How the drive lists files. */
export const DEFAULT_VIEWS = ['list', 'grid'] as const
export type DefaultView = (typeof DEFAULT_VIEWS)[number]

export function isDefaultView(v: unknown): v is DefaultView {
  return typeof v === 'string' && (DEFAULT_VIEWS as readonly string[]).includes(v)
}

/**
 * Bounds on the numeric and open-ended fields.
 *
 * Not cosmetic limits. `users.settings` rides along on reads of the `users`
 * row, which happen on authenticated requests, so an unbounded map is a blob
 * that grows forever on a hot path — the same reason SQL history was sent to
 * localStorage rather than into this column. The ranges are generous enough
 * that no real preference hits them and tight enough that a hostile or buggy
 * client cannot make the row large.
 */
export const FONT_SIZE_RANGE = { min: 8, max: 72 } as const
export const TAB_SIZE_RANGE = { min: 1, max: 16 } as const
/** Per-map entry cap, and the longest key or string value inside one. */
export const SETTINGS_MAP_MAX_ENTRIES = 200
export const SETTINGS_MAP_MAX_STRING = 200

/**
 * The UI half of `users.settings`.
 *
 * EVERY field is optional and absence means "use the client's default" — the
 * column is sparsely populated by design and most users will have only the one
 * or two keys they have actually changed. A required field here would make
 * every existing row invalid.
 */
export interface UserUiSettings {
  theme?: ThemeSettings
  showHiddenFiles?: boolean
  defaultView?: DefaultView
  fontSize?: number
  tabSize?: number
  /** Format Document on every save (Ctrl/Cmd+S) in the text editors — off unless the person turns it on (#987). */
  formatOnSave?: boolean
  /** Preferred editor per extension, e.g. `{ xlsx: 'onlyoffice-sheet' }`. */
  preferredEditors?: Record<string, string>
  /** Last-used HTML-export options (enum/number values only — no ids or paths). */
  exportDefaults?: Record<string, string | number | boolean>
}

/** The recognized UI keys, for a caller that needs to ask "is any of these set?" */
export const USER_UI_SETTING_KEYS = [
  'theme', 'showHiddenFiles', 'defaultView', 'fontSize', 'tabSize', 'formatOnSave',
  'preferredEditors', 'exportDefaults',
] as const satisfies readonly (keyof UserUiSettings)[]
